"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface UseCase {
  title: string;
  body: string;
  metric: string;
  icon: LucideIcon;
  accent: string;
}

export interface UseCasesSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  cases: UseCase[];
  accent: string;
}

export function UseCasesSection({
  id = "use-cases",
  eyebrow,
  title,
  cases,
  accent,
}: UseCasesSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className="mt-12 space-y-5">
          {cases.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.title} delay={i * 80}>
                <div
                  className="glass rounded-2xl p-7 flex flex-col md:flex-row gap-6"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 14%, transparent)`,
                  }}
                >
                  <div className="shrink-0">
                    <span
                      className="grid place-items-center h-14 w-14 rounded-2xl"
                      style={{
                        background: `color-mix(in oklch, ${c.accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-6 w-6" style={{ color: c.accent }} />
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg">{c.title}</h3>
                    <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{c.body}</p>
                    <div
                      className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                      style={{
                        background: `color-mix(in oklch, ${c.accent} 12%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 30%, transparent)`,
                      }}
                    >
                      <span className="text-xs font-bold" style={{ color: c.accent }}>
                        {c.metric}
                      </span>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
